import { useState } from 'react';
import { useNavigate } from 'react-router';
import { ApiError, post } from '../api.js';
import { useOffline } from '../offline/context.js';
import { useSignedIn } from '../session.js';
import { Dialog, Notice } from './ui.js';

/**
 * Sign out. It needs the network, so the session also ends on the server;
 * with changes not sent yet, it asks first: they stay encrypted on this
 * device and are sent at the next sign-in of the same person here.
 */
export function SignOutButton({ className = 'btn' }: { className?: string }) {
  const { setSession } = useSignedIn();
  const { counts } = useOffline();
  const navigate = useNavigate();
  const [asking, setAsking] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const unsent = counts.pending + counts.rejected;

  async function signOut() {
    setAsking(false);
    setError(null);
    try {
      await post('/auth/sign-out');
    } catch (err) {
      if (err instanceof ApiError && (err.code === 'offline' || err.status >= 500)) {
        setError('Signing out needs the network. Try again when you are online.');
        return;
      }
    }
    setSession({ state: 'signed_out' });
    navigate('/sign-in', { replace: true });
  }

  return (
    <>
      <button
        type="button"
        className={className}
        onClick={() => (unsent > 0 ? setAsking(true) : void signOut())}
      >
        Sign out
      </button>
      {error && <Notice kind="error">{error}</Notice>}
      {asking && (
        <Dialog
          small
          title="Sign out?"
          onClose={() => setAsking(false)}
          footer={
            <>
              <button type="button" className="btn" onClick={() => setAsking(false)}>
                Cancel
              </button>
              <button type="button" className="btn primary" onClick={() => void signOut()}>
                Sign out
              </button>
            </>
          }
        >
          <p className="note">
            {unsent} {unsent === 1 ? 'change is' : 'changes are'} not sent yet. They stay encrypted
            on this device and are sent the next time you sign in here.
          </p>
        </Dialog>
      )}
    </>
  );
}
